<template>
  <div>
    <v-sheet color="grey-8" class="py-3">
      <div class="d-flex px-4">
        <div class="d-flex align-center" style="width: 800px; max-width: 100%; gap: 8px;">
          <div>搜尋</div>
          <v-text-field
            v-model="searchQuery"
            label="搜尋關鍵字：病患名稱 / 手機 / 看診時間(YYYY-MM-DD) / 訊息名稱"
            clearable
            hide-details
            dense
            outlined
            @click:clear="clearSearch"
            @keydown.enter="triggerSearch"
          />
          <v-btn icon color="primary" @click="triggerSearch">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </div>
      </div>
    </v-sheet>
    <v-alert v-if="listError" type="error" text dense class="ma-4" role="alert">
      {{ listError }}
      <v-btn text small :disabled="loading" @click="fetchData">重新查詢</v-btn>
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      fixed-header
      height="calc(100vh - 256px)"
      :footer-props="{itemsPerPageOptions: [10, 20, 50, 100]}"
    >
      <template v-slot:item.reserve_start="{ item }">
        {{ convertToTimezone(item.reserve_start, 'Asia/Taipei', 'YYYY-MM-DD HH:mm') }}
      </template>
      <template v-slot:item.sending_progress="{ item }">
        <div v-if="item.messages && item.messages.length">
          <div v-for="(msg, index) in item.messages" :key="msg.id">
            {{ item.tag_name }} - {{ (msg.title != null && msg.title !== '') ? msg.title : index + 1 }}: 
            {{ convertToTimezone(msg.scheduled_send_at, 'Asia/Taipei', 'YYYY-MM-DD HH:mm') }}
            <span :class="`${statusColor(msg.send_status)}--text`">{{ formatStatus(msg.send_status) }}</span>
          </div>
        </div>
      </template>

      <template v-slot:item.note="{ item }">
        <span>{{ (item.note && item.note.length > 20) ? item.note.substr(0, 20) + '...' : item.note }}</span>
        <v-btn icon color="primary" @click="openCrmNoteDialog(item)">
          <v-icon x-small>mdi-pencil</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn v-if="isEditable(item)" :disabled="loading || saving" small outlined color="primary" @click="openEditDialog(item)">
          編輯發送時間
        </v-btn>
      </template>
    </v-data-table>

    <care-schedule-edit-dialog
      :value="editDialog" :saving="saving" :current-item="currentItem"
      :editable-messages="editableMessages" :min-date="minDate"
      @toggle-edit="toggleEditMessage" @save="saveChanges" @close="closeEditDialog"
    />

    <v-dialog v-if="editingCrmNoteItem" v-model="crmNoteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>修改院內備註</v-card-title>
        <v-card-text>
          <v-textarea v-model="editingCrmNoteItem.note" hide-details outlined no-resize/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="blue" text @click="updateCrmNote">確定</v-btn>
          <v-btn text @click="crmNoteDialog = false">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getCustomizeCareSendQueueAPI, updateCareSendQueueCrmNoteAPI } from "@/api";
import { convertToTimezone } from "@/utils/filters";

import CareScheduleEditDialog from '@/components/PostOperativeCare/CareScheduleEditDialog.vue';
import careScheduleEditing from '@/mixins/careScheduleEditing';

export default {
  components: { CareScheduleEditDialog },
  mixins: [careScheduleEditing],
  name: "PendingList",
  data() {
    return {
      searchQuery: '',
      loading: true,
      listError: '',
      fetchRequestId: 0,
      items: [],
      totalItems: 0,
      options: {},
      headers: [
        { text: '病患姓名', value: 'patient_name', sortable: false },
        { text: '病患手機', value: 'patient_phone', sortable: false },
        { text: '看診時間', value: 'reserve_start', sortable: false },
        { text: '約診醫師', value: 'doctor_name', sortable: false },
        { text: '約診診所', value: 'clinic_name', sortable: false },
        { text: '訊息名稱', value: 'tag_name', sortable: false },
        { text: '發送時間', value: 'sending_progress', sortable: false, width: '25%' },
        { text: '院內備註', value: 'note', sortable: false, width: 150 },
        { text: '操作', value: 'actions', sortable: false },
      ],
      crmNoteDialog: false,
      editingCrmNoteItem: null,

    };
  },
  computed: {
    ...mapGetters(["axiosParamData"]),
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },
  methods: {
    convertToTimezone,
    async fetchData() {
      const requestId = ++this.fetchRequestId;
      this.loading = true;
      this.listError = '';
      try {
        const { page, itemsPerPage } = this.options;
        const params = {
          ...this.axiosParamData,
          page,
          per_page: itemsPerPage,
        };
        if (this.searchQuery && this.searchQuery.trim()) {
          params.q = this.searchQuery.trim();
        }
        const res = await getCustomizeCareSendQueueAPI(params);
        if (requestId !== this.fetchRequestId) return;
        this.items = res.data.data;
        this.totalItems = res.data.meta.total;
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        this.items = [];
        this.totalItems = 0;
        this.listError = '讀取排程失敗，請重新查詢最新資料。';
      } finally {
        if (requestId === this.fetchRequestId) this.loading = false;
      }
    },
    triggerSearch() {
      this.options.page = 1;
      this.fetchData();
    },
    clearSearch() {
      this.searchQuery = '';
      this.triggerSearch();
    },
    openCrmNoteDialog(item) {
      this.editingCrmNoteItem = JSON.parse(JSON.stringify(item));
      this.crmNoteDialog = true;
    },
    async updateCrmNote() {
      try {
        await updateCareSendQueueCrmNoteAPI(this.editingCrmNoteItem.group_uuid, {
          ...this.axiosParamData,
          note: this.editingCrmNoteItem.note
        });
        this.crmNoteDialog = false;
        this.editingCrmNoteItem = null;
        await this.fetchData();
      } catch (error) {
        console.error('Error updating crm note:', error);
        this.$notify.error('更新備註失敗，請稍後再試。');
      }
    },
  },
};
</script>
